/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Mon Nov 20 2023
 **/

import { useShallow } from "zustand/react/shallow";

import { Avatar, Column, Icon, Loader, Row, Typography } from "@/frameworks/designStuff/components";
import { Drawer } from "@/frameworks/designStuff/compounds";
import { Maybe } from "@/frameworks/libraries/components";
import { Utility, ChronoUtils } from "@/frameworks/libraries/utilities";
import { _P } from "@rfice/language";
import { MessageList, useChatRoomActions } from "@/frameworks/core/chat";
import { ChatRenderer, VoteRenderer } from "@/frameworks/core/components";
import { useIntersectionObserver } from "@/frameworks/libraries/hooks";
import { useSidebarActions } from "@/frameworks/core/hooks";
import { SidebarID } from "@/frameworks/core/commons";
import { useChatDataStore } from "@/modules/chat/store";

import * as S from "../emotion";

export default function SearchedConversation({
	searchText,
	conversations,
	onIntersecting
}: {
	searchText: string;
	conversations: WebServices.Chat.Search.Data["content"] | undefined;
	onIntersecting: () => void;
}) {
	const chatRoomActions = useChatRoomActions();
	const sidebarActions = useSidebarActions();
	const { setSearchTextSelection, set_totalChat_search_area_open } = useChatDataStore(
		useShallow((state) => ({
			setSearchTextSelection: state.set_chat_search_selected,
			set_totalChat_search_area_open: state.set_totalChat_search_area_open
		}))
	);
	const handleClickConversation = ({
		channelID,
		spaceID,
		searchChatTextId
	}: {
		channelID: string;
		spaceID?: string;
		searchChatTextId: string;
	}) => {
		setSearchTextSelection(searchChatTextId);
		set_totalChat_search_area_open(false);
		if (spaceID) {
			chatRoomActions.common.open({ channelID, type: "Space", spaceID });
		} else {
			chatRoomActions.common.open({ channelID, type: "DM" });
		}
		sidebarActions.hide(SidebarID.ChatSearch);
	};

	const { observerRef } = useIntersectionObserver(
		({ isIntersecting }) => {
			if (isIntersecting) {
				onIntersecting();
			}
		},
		[onIntersecting]
	);

	if (!conversations) {
		return (
			<Drawer.Body css={{ width: "100%", height: "100%" }}>
				<Column verticalAlign="center" horizonAlign="center" css={{ width: "100%", height: "100%" }}>
					<Loader />
				</Column>
			</Drawer.Body>
		);
	}

	return (
		<Drawer.Body css={{ width: "100%", height: "100%" }}>
			<Maybe
				test={conversations.length > 0}
				fallback={
					<Column verticalAlign="center" horizonAlign="center" css={{ width: "100%", height: "100%" }}>
						<Icon name="ToastError24" fill="#000" size={52} />
						<Typography typoSize="Body3" typoColor="neutral_70_100" align="center">
							{_P("검색 결과가 없습니다.<br />다른 검색어를 입력해주세요.")}
						</Typography>
					</Column>
				}
			>
				<Column gap={8} css={{ padding: "8px 60px" }}>
					{conversations?.map((conversation, index) => {
						if (!Utility.json.isJson(conversation.content)) return;
						const emphasisText = `<span id='search-text'>${searchText}</span>`;

						const vote = {
							...conversation.vote,
							title: conversation.vote?.title.replace(searchText, emphasisText)
						} as MessageList[string]["vote"];

						const parseContent =
							conversation.messageType === "VOTE"
								? (JSON.parse(conversation.content) as ChatServices.Format2.VoteMessage)
								: (JSON.parse(conversation.content) as ChatServices.Format2.Message);

						let content = parseContent;
						if (conversation.messageType !== "VOTE" && parseContent.elements[0].type === "text") {
							content = {
								...parseContent,
								elements: [
									{
										...parseContent.elements[0],
										text: parseContent.elements[0].text.replace(searchText, emphasisText)
									}
								]
							};
						}

						const targetIndexOfRichtext = content.elements.findIndex((v) => {
							if (v.type === "text") {
								return typeof v.text === "string" && v.text.includes(emphasisText);
							}
							if (v.type === "user") {
								return v.name.includes(emphasisText);
							}
							if (v.type === "codeBlock") {
								return v.code.includes(emphasisText);
							}
							if (v.type === "emoticon") {
								return v.text.includes(emphasisText);
							}
							if (v.type === "file") {
								return v.title.includes(emphasisText);
							}
							if (v.type === "image") {
								return v.title?.includes(emphasisText);
							}
							if (v.type === "link") {
								return v.title.includes(emphasisText);
							}
						});

						return (
							<S.SearchedConversationItem
								key={conversation.roomMessageId}
								gap={12}
								ref={index === conversations.length - 5 ? observerRef : null}
								onClick={() =>
									handleClickConversation({
										channelID: conversation.roomId,
										spaceID: conversation.spaceId,
										searchChatTextId: conversation.roomMessageId
									})
								}
							>
								<Avatar
									kind="people_32"
									type="image"
									src={conversation.user?.profileImagePath}
									text={String(conversation.user?.name)}
									status={(() => {
										switch (conversation.user?.activeStatus) {
											case "ACTIVE":
												return "Green";
											case "OFFLINE":
												return "Gray";
											case "FOCUS":
												return "Red";
											case "AWAY":
												return "Yellow";
											default:
												return undefined;
										}
									})()}
								/>
								<S.ContentWrapper>
									<Row gap={8} verticalAlign="center">
										<Typography typoSize="Title1" typoColor="neutral_80_100">
											{conversation.user?.name}
										</Typography>
										<Typography typoSize="Body4" typoColor="neutral_60_100">
											{ChronoUtils.formatTimeChatTime(conversation.timestamp)}
										</Typography>
									</Row>
									<S.EmphasisSearchText tag="div">
										<Maybe
											test={content.type === "richtext"}
											fallback={
												<VoteRenderer
													chatForm={content as ChatServices.Format2.VoteMessage}
													vote={vote}
													roomID={conversation.roomId}
													spaceID={conversation.spaceId}
													voteType={content.elements?.[0]?.key}
													isSearch={true}
												/>
											}
										>
											<ChatRenderer
												chatForm={content as ChatServices.Format2.Message}
												startLine={targetIndexOfRichtext > 3 ? targetIndexOfRichtext - 3 : 0}
												maxLine={targetIndexOfRichtext + 3}
											/>
										</Maybe>
									</S.EmphasisSearchText>
								</S.ContentWrapper>
							</S.SearchedConversationItem>
						);
					})}
				</Column>
			</Maybe>
		</Drawer.Body>
	);
}
